import React, {useEffect, useState} from 'react';
import {Box, Typography, Grid, Card, CardContent, Button, Dialog} from '@mui/material';
import useStyles from "../../../assets/styles/AssessmentPageStyles";
import Icon1 from "../../../assets/images/DQProImages/Graphic14.png"
import Icon2 from "../../../assets/images/DQProImages/Graphic15.png"
import Icon3 from "../../../assets/images/DQProImages/Graphic17.png"
import Icon4 from "../../../assets/images/DQProImages/Graphic18.png"
import CheckIcon from '@material-ui/icons/Check';
import {useHistory} from "react-router-dom";
import {
    getInsiderAssessmentDescriptionService,
    getInsiderPackageService,
    getInsiderUserGroupsService
} from "../../../services/assessments.service";
import LoaderWithBackDrop from "../../../components/LoaderWithBackDrop/LoaderWithBackDrop.comp";
import { useAuth } from "../../../context/auth";
import { USER_ACCESS_LEVEL } from "../../../utils/constants";
import NotFound from "../../404/404.page";

// Same accent + card language as the redesigned Assessment Preview page and
// the DQ Pro V2 "Assess" tab (AssessmentTab.comp.jsx) — brand CSS vars so it
// also follows dark mode automatically, no manual isDark branching needed.
const ACCENT = 'var(--brand-primary, #FF6900)';

const DQProAssessments = () => {
    const classes = useStyles();
    const history=useHistory();
    const { authUser } = useAuth();

    const [loading, setLoading] = useState(false);
    const [selectedAssessment, setSelectedAssessment] = React.useState(null);
    const [selectedIndicators, setSelectedIndicators] = React.useState([]);
    const [totalDuration, setTotalDuration] = React.useState(0);
    const [assessments,setAssessments] = useState([]);

    const Icons = [Icon1, Icon2, Icon3, Icon4];
    const mapUserGroupsToCard = (userGroup, idx) => ({
        icon: Icons[idx % Icons.length],
        title: userGroup.title,
        subtitle: userGroup.sub_title ? `(${userGroup.sub_title})` : '',
        description: userGroup.description,
        code: userGroup.code,
        packages: []
    });

    useEffect(() => {
        const fetchInsiderModules = async () => {
            try {
                const response = await getInsiderUserGroupsService();
                if (response.data.userGroups) {
                    const cards = response.data.userGroups.map(mapUserGroupsToCard);
                    setAssessments(cards);
                }
            } catch (error) {
                console.error('Error fetching assessments:', error);
            }
        };
        fetchInsiderModules().then(r => console.log(r));
    }, []);

    if (authUser?.access_level === USER_ACCESS_LEVEL.PARTIAL_ACCESS_DQ_INSIDER_PORTAL_SINGLE_INSTITUTE) {
        return <NotFound />;
    }

    const normaliseTree = (tree) =>
        Object.entries(tree).map(([pkgName, pkgObj]) => {
            const { description: pkgDesc, ...modulesObj } = pkgObj;
            return {
                title: { name: pkgName, desc: pkgDesc },
                modules: Object.entries(modulesObj).map(([modName, modObj]) => {
                    const { description: modDesc, ...indicatorsObj } = modObj;
                    return {
                        title: { name: modName, desc: modDesc },
                        indicators: Object.entries(indicatorsObj).map(([indName, indObj]) => ({
                            title     : { name: indName, desc: indObj.description },
                            questions : indObj.no_of_questions,
                            duration  : indObj.duration,
                            indicator_code: indObj.indicator_code,
                            dqco_no: indObj.dqco_no
                        }))
                    };
                })
            };
        });


    const handleCardClick = async (assessment) => {
        try {
            const { data } = await getInsiderPackageService(assessment.code);
            const packages  = normaliseTree(data);
            setSelectedAssessment({ ...assessment, packages });
            setSelectedIndicators([]);
            setTotalDuration(0);
        } catch (err) {
            console.error(err);
            setSelectedAssessment({ ...assessment, packages: [] });
        }
    };

    const handleIndicatorSelect = (indicator, packageIndex, moduleIndex, indicatorIndex) => {
        const indicatorKey = `${packageIndex}-${moduleIndex}-${indicatorIndex}`;

        if (selectedIndicators.includes(indicatorKey)) {
            setSelectedIndicators(selectedIndicators.filter(item => item !== indicatorKey));
            setTotalDuration(totalDuration - indicator.duration);
        } else {
            setSelectedIndicators([...selectedIndicators, indicatorKey]);
            setTotalDuration(totalDuration + indicator.duration);
        }
    };

    return (
        <>
            <LoaderWithBackDrop loading={loading} />
            <Box className={classes.pageContainer} style={{marginTop:'auto'}}>
             {!selectedAssessment ? (
               <>
                <Typography variant="h3" className={classes.title} sx={{ mb: 2, textAlign: 'center' }}>
                    AI Readiness Assessments Available
                </Typography>
                <Typography variant="h5" sx={{ textAlign: 'center', mb: 4 }}>
                    Select a target audience you want to build an assessment for.
                </Typography>
                <Grid container spacing={4} justifyContent="center" sx={{ mt: 4 }}>
                    {assessments.map((assessment, index) => (
                        <Grid item xs={12} sm={6} md={6} lg={3} key={index}>
                            <Card className={classes.card}>
                                <img src={assessment.icon} alt="Assessment Icon" className={classes.icon} />

                                <Typography className={classes.cardTitle}>
                                    {assessment.title}
                                </Typography>
                                    <Typography className={classes.subtitle} sx={{ mb: 1, minHeight: '24px' }}>
                                        {assessment.subtitle}
                                    </Typography>
                                    <Typography className={classes.description}>
                                        {assessment.description}
                                    </Typography>
                                    <Button
                                        className={classes.button}
                                        variant="contained"
                                        onClick={() => handleCardClick(assessment)}
                                    >
                                        Select
                                    </Button>
                                </Card>
                            </Grid>
                        ))}
                </Grid>
               </>
                ) : (
                 <Box sx={{ mt: {xs:'75px', lg:4 }}}>
                     <Typography variant="h3" className={classes.title} sx={{ mb: 2, textAlign: 'center' }}>
                         {selectedAssessment.title} Assessment
                     </Typography>
                     <Typography variant="h5" sx={{fontSize:{xs:'1.2rem', md:'1.5rem'}, textAlign: 'center', mb: 2,}}>
                         Select what you want to assess below. You can select up to 45 total minutes per each assessment.<br/>
                         If your selections total more than 45 minutes, you will need to break it up into 2 separate assessments.
                     </Typography>
                     <Typography sx={{ textAlign: 'center', fontSize:{md:'16px',lg:'19px'},lineHeight:'1.2', mb: 4 }}>
                         <b>Please Note:</b> Each assessment you create uses <b>one license per learner</b>. For example, if you create <b>two separate assessments</b>,<br/> each one will count as <b>a separate license</b>, even if assigned to the same learner.
                     </Typography>

                     {/* Spacer reserves the vertical space the fixed pill below floats over,
                         so the table doesn't render underneath it. */}
                     <Box sx={{ height: { xs: '84px', sm: '68px' } }} />

                     <Box
                         sx={{
                             position: 'fixed',
                             top: { xs: '70px', lg: '20px' },
                             left: '50%',
                             transform: 'translateX(-50%)',
                             zIndex: 50,
                             display: 'table',
                             flexDirection: 'column',
                             justifyContent: 'center',
                             alignItems: 'center',
                             p: 2,
                             borderRadius: '15px',
                             backgroundColor: 'var(--brand-surface, #f5f4ed)',
                             border: '1px solid var(--brand-border, transparent)',
                             boxShadow: 3,
                         }}
                     >
                         <Typography variant="h6" sx={{ textAlign: 'left', fontWeight:'regular', color: 'var(--brand-text, #111827)' }}>
                             Based on your selections, your assessment is approximately <strong>{totalDuration}</strong> minutes long.
                         </Typography>

                         {totalDuration > 45 && (
                             <Typography color="error" sx={{ textAlign: 'center', display: 'block' }}>
                                 Consider splitting into multiple assessments
                             </Typography>
                         )}
                     </Box>

                     <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                         {(selectedAssessment.packages || []).map((pkg, pkgIndex) => (
                             <Box key={pkgIndex} sx={{
                                 border: '1.5px solid var(--brand-border, #EBEBEB)',
                                 borderRadius: '16px',
                                 backgroundColor: 'var(--brand-surface, #fff)',
                                 overflow: 'hidden',
                                 boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                             }}>
                                 {/* Package header */}
                                 <Box sx={{ px: { xs: 2, sm: 3 }, py: 2.25, backgroundColor: 'var(--brand-bg, #F9FAFB)', borderBottom: '1px solid var(--brand-border, #F0F0F0)' }}>
                                     <Typography sx={{ fontSize: 17, fontWeight: 800, color: 'var(--brand-text, #111827)' }}>
                                         {pkg.title.name}
                                     </Typography>
                                     {pkg.title.desc && (
                                         <Typography sx={{ fontSize: 13, color: 'var(--brand-text-muted, #6B7280)', mt: 0.5, lineHeight: 1.5 }}>
                                             {pkg.title.desc}
                                         </Typography>
                                     )}
                                 </Box>

                                 <Box sx={{ p: { xs: 2, sm: 3 }, display: 'flex', flexDirection: 'column', gap: 3 }}>
                                     {pkg.modules.map((module, modIndex) => (
                                         <Box key={modIndex}>
                                             {/* Module sub-header */}
                                             <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
                                                 <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: ACCENT, flexShrink: 0, mt: '7px' }} />
                                                 <Box>
                                                     <Typography sx={{ fontSize: 15, fontWeight: 700, color: 'var(--brand-text, #111827)' }}>
                                                         {module.title.name}
                                                     </Typography>
                                                     {module.title.desc && (
                                                         <Typography sx={{ fontSize: 12, color: 'var(--brand-text-muted, #6B7280)', mt: 0.25 }}>
                                                             {module.title.desc}
                                                         </Typography>
                                                     )}
                                                 </Box>
                                             </Box>

                                             {/* Indicator cards — same selectable-option language as the
                                                 Assessment Preview / Assess-tab question options. */}
                                             <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pl: { xs: 0, sm: 2.75 } }}>
                                                 {module.indicators.map((indicator, indIndex) => {
                                                     const rowKey = `${pkgIndex}-${modIndex}-${indIndex}`;
                                                     const isSelected = selectedIndicators.includes(rowKey);
                                                     return (
                                                         <Box
                                                             key={rowKey}
                                                             onClick={() => handleIndicatorSelect(indicator, pkgIndex, modIndex, indIndex)}
                                                             sx={{
                                                                 display: 'flex',
                                                                 alignItems: 'flex-start',
                                                                 gap: 2,
                                                                 p: '14px 18px',
                                                                 borderRadius: '12px',
                                                                 cursor: 'pointer',
                                                                 border: isSelected ? `2px solid ${ACCENT}` : '1px solid var(--brand-border, #E5E7EB)',
                                                                 backgroundColor: isSelected ? 'rgba(255,105,0,0.06)' : 'var(--brand-surface, #ffffff)',
                                                                 transition: 'all 0.15s ease',
                                                                 '&:hover': { borderColor: ACCENT, boxShadow: '0px 2px 8px rgba(0,0,0,0.04)' },
                                                             }}
                                                         >
                                                             <Box sx={{
                                                                 width: 24, height: 24, borderRadius: '50%', flexShrink: 0, mt: '2px',
                                                                 display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                 border: isSelected ? 'none' : '1.5px solid var(--brand-border, #D1D5DB)',
                                                                 backgroundColor: isSelected ? ACCENT : 'transparent',
                                                             }}>
                                                                 {isSelected && <CheckIcon style={{ color: '#fff', fontSize: 16 }} />}
                                                             </Box>
                                                             <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                 <Typography sx={{ fontSize: 15, fontWeight: 700, color: 'var(--brand-text, #111827)' }}>
                                                                     {indicator.title.name}
                                                                     {indicator.dqco_no && (
                                                                         <Box component="span" sx={{ color: ACCENT, fontWeight: 600 }}> ({indicator.dqco_no})</Box>
                                                                     )}
                                                                 </Typography>
                                                                 {indicator.title.desc && (
                                                                     <Typography sx={{ fontSize: 13, color: 'var(--brand-text-muted, #6B7280)', mt: 0.5, lineHeight: 1.5 }}>
                                                                         {indicator.title.desc}
                                                                     </Typography>
                                                                 )}
                                                                 <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                                                                     <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'var(--brand-text-muted, #9CA3AF)' }}>
                                                                         {indicator.questions} question{indicator.questions === 1 ? '' : 's'}
                                                                     </Typography>
                                                                     <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'var(--brand-text-muted, #9CA3AF)' }}>
                                                                         {indicator.duration} min
                                                                     </Typography>
                                                                 </Box>
                                                             </Box>
                                                         </Box>
                                                     );
                                                 })}
                                             </Box>
                                         </Box>
                                     ))}
                                 </Box>
                             </Box>
                         ))}
                     </Box>
                     <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                     <Button
                                 className={classes.button}
                                 variant="contained"
                                 sx={{width:{xs:'35%', md:'21%'}}}
                                 disabled={selectedIndicators.length === 0 || totalDuration > 45}
                                 onClick={async () => {
                                     const indicatorObjs = selectedIndicators.map(k => {
                                         const [pkgIdx, modIdx, indIdx] = k.split('-').map(Number);
                                         return selectedAssessment.packages[pkgIdx]
                                             .modules[modIdx].indicators[indIdx];
                                     });

                                     const indicators_descriptions = indicatorObjs.map(i => i.title.desc);

                                     // let response = null;
                                     // try {
                                     //     setLoading(true);
                                     //     response = await getInsiderAssessmentDescriptionService(selectedAssessment.code, indicators_descriptions);
                                     //        console.log("Response:", response.data.description);
                                     // } catch (err) {
                                     //     setLoading(false);
                                     //     console.error('Error fetching indicator descriptions:', err);
                                     // } finally {
                                     //     setLoading(false);
                                     // }

                                     const buildData = {
                                         user_group: selectedAssessment.code,
                                         image_url: selectedAssessment.icon,
                                         indicator_codes: indicatorObjs.map(i => i.indicator_code),
                                         length_questions: indicatorObjs.reduce((t, i) => t + i.questions, 0),
                                         time_to_complete: indicatorObjs.reduce((t, i) => t + i.duration, 0),
                                         more_info: indicatorObjs.map(i => ({
                                             title: i.title.name,
                                             description: i.title.desc
                                         })),
                                         indicators_descriptions,
                                     };

                                     history.push(
                                         '/dq_pro/products/assessments/name',
                                         {buildData}
                                     )
                                 }}
                             >
                                 Build Assessment
                             </Button>
                     </Box>
                 </Box>
             )}
            </Box>
        </>
    );
};

export default DQProAssessments;
